import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { apiClient } from '../lib/api';
import { Sparkles, Loader2, Mail, ArrowLeft, CheckCircle } from 'lucide-react';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Veuillez entrer une adresse email valide');
      return;
    }

    setLoading(true);

    try {
      await apiClient.post('/auth/request-password-reset', { email });
      setSubmitted(true);
      toast.success('Si cet email existe, un lien de réinitialisation a été envoyé.');
    } catch (err) {
      // Even on error, show success for security (don't reveal if email exists)
      setSubmitted(true);
      toast.success('Si cet email existe, un lien de réinitialisation a été envoyé.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      backgroundImage: 'url(https://images.unsplash.com/photo-1762433813475-e6b761cc23d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHNoYXBlcyUyMHNvZnQlMjBwYXN0ZWwlMjBjb2xvcnN8ZW58MHx8fHwxNzY1OTAxMTkzfDA&ixlib=rb-4.1.0&q=85)',
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }}>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 mb-4 hover:opacity-80">
            <Sparkles className="w-10 h-10 text-primary" />
            <h1 className="text-4xl font-heading font-bold text-foreground">AI Marketing Engine</h1>
          </button>
          <p className="text-muted-foreground">Réinitialisation du mot de passe</p>
        </div>

        <Card className="backdrop-blur-xl bg-white/90 border-white/20 shadow-2xl">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Mail className="w-5 h-5" />
              {submitted ? 'Vérifiez vos emails' : 'Mot de passe oublié ?'}
            </CardTitle>
            <CardDescription>
              {submitted 
                ? 'Nous vous avons envoyé un lien de réinitialisation.'
                : 'Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {submitted ? (
              /* Success State */
              <div className="text-center py-6">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <p className="text-green-700 font-medium mb-2">
                  Email envoyé !
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  Si un compte existe pour <span className="font-medium">{email}</span>, vous recevrez un lien de réinitialisation dans quelques instants.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>📧 Vérifiez votre boîte de réception</strong><br />
                    N'oubliez pas de vérifier votre dossier spam si vous ne voyez pas l'email dans les prochaines minutes.
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => {
                      setSubmitted(false);
                      setEmail('');
                    }}
                  >
                    Essayer avec un autre email
                  </Button>
                  <Button 
                    variant="ghost" 
                    className="w-full"
                    onClick={() => navigate('/auth')}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour à la connexion
                  </Button>
                </div>
              </div>
            ) : (
              /* Request Form */
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Adresse email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="vous@exemple.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="pl-10"
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    'Envoyer le lien'
                  )}
                </Button>

                <Button 
                  type="button" 
                  variant="ghost" 
                  className="w-full"
                  onClick={() => navigate('/auth')}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour à la connexion
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Footer links */}
        <div className="mt-6 text-center text-sm text-muted-foreground">
          <a href="/privacy-policy" className="hover:underline">Confidentialité</a>
          {' • '}
          <a href="/terms" className="hover:underline">CGU</a>
        </div>
      </div>
    </div>
  );
}
